import type {
  MenuProps as MuiMenuProps,
  SelectProps as MuiSelectProps,
  TooltipProps,
} from "@mui/material";
import { InputAdornment, Select as MuiSelect } from "@mui/material";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { v4 } from "uuid";

import { Item } from "@citrine/atoms/item";

import { ErrorOutlineIcon } from "../icons";
import { Tooltip } from "../tooltip";

import * as sx from "./select.sx";
import type { ContentProps } from "./use-menu-items";
import { useMenuItems } from "./use-menu-items";
import type { SelectOption } from "./use-option-objects";
import { useOptionObjects } from "./use-option-objects";

export { sortByValue } from "./use-option-objects";
export type { SelectOption } from "./use-option-objects";

type TooltipOptions =
  | {
      tooltipContent?: never;
      tooltipPlacement?: never;
    }
  | {
      tooltipContent: TooltipProps["title"];
      tooltipPlacement?: TooltipProps["placement"];
    };

export type BaseSelectProps = Omit<
  MuiSelectProps,
  "children" | "error" | "multiple" | "notched" | "value" | "variant"
> &
  Partial<Pick<MuiSelectProps, "variant">> & {
    error?: string;
  } & TooltipOptions;

export type SelectProps<Value extends string | undefined = string> =
  BaseSelectProps &
    Omit<ContentProps<Value>, "options"> & {
      options: readonly SelectOption<Value>[];
      description?: ReactNode;
    };

/**
 * Extends the Select component with a couple of tweaks:
 *  - allows for specifying an array of string `options` in lieu of providing
 *    children - do this if you don't need custom rendering on the dropdown options
 *  - transparent background on the `<input>` element
 *  - adds basic Tooltip support - removes the need for the consumer to manage
 *    Tooltip state - re: https://stackoverflow.com/questions/50107641/
 */
export function Select<Value extends string = string>({
  options,
  tooltipContent,
  tooltipPlacement,
  className,
  color,
  description,
  disabled,
  displayEmpty = true,
  endAdornment,
  error,
  fullWidth,
  id: inputId,
  label,
  MenuProps,
  renderValue,
  startAdornment: startAdornmentProp,
  size,
  ...rest
}: SelectProps<Value>) {
  const [id] = useState(inputId || (() => v4()));

  // We need to manually open and close the Tooltip or else the Tooltip stays
  // there after opening and closing the Select menu
  // https://stackoverflow.com/questions/50107641/
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const closeTooltip = useCallback(
    () => setTooltipOpen(false),
    [setTooltipOpen],
  );
  const openTooltip = useCallback(() => setTooltipOpen(true), [setTooltipOpen]);

  const optionObjects = useOptionObjects(options);

  const menuItems = useMenuItems({
    color,
    options: optionObjects,
    multiple: rest.multiple,
    value: rest.value,
  } as ContentProps<Value>);

  const selectProps = {
    ...rest,
    children: [description, menuItems],
    label,
    size,
    // Allow value to be undefined when passed in, but transform it into "" or
    // [] since we are always using <MuiSelect /> as a controlled component, and
    // MUI interprets a value of undefined as an uncontrolled component.
    value: rest.value ?? (rest.multiple ? [] : ""),
  };

  const renderValueCallback = useMemo(() => {
    let renderOne: (value: Value) => React.ReactNode;
    if (renderValue) {
      renderOne = renderValue;
    } else {
      // create a lookup map from item value to children to be used as the default renderValue function
      // this is essentially the default functionality, but allows us to overload it
      // with custom adornment handling
      const itemLookup = optionObjects.reduce<
        Partial<Record<Value, React.ReactNode>>
      >((acc, option) => {
        acc[option.value as Value] = <Item {...option} />;
        return acc;
      }, {});

      renderOne = (value) => itemLookup[value];
    }

    return (value: Value | Value[]) => {
      const renderedValue =
        // if no value is selected, render nothing because the label will be there
        !value?.length && !displayEmpty ? null : Array.isArray(value) ? (
          <sx.Chips>
            {value.map((v) => (
              <sx.Chip key={v.toString()} label={renderOne(v)} size={size} />
            ))}
          </sx.Chips>
        ) : (
          renderOne(value)
        );

      const startAdornment = error ? (
        <ErrorOutlineIcon fontSize="small" />
      ) : (
        startAdornmentProp
      );

      // render adornments here rather than on the MUI Select because only the result of this fn is clickable
      return (
        <>
          {startAdornment ? (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ) : null}
          <sx.Value>{renderedValue}</sx.Value>
          {endAdornment ? (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ) : null}
        </>
      );
    };
  }, [
    endAdornment,
    error,
    optionObjects,
    renderValue,
    displayEmpty,
    size,
    startAdornmentProp,
  ]);

  const menuProps = useMemo<Partial<MuiMenuProps>>(
    () => ({
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      sx: sx.menu,
      ...MenuProps,
    }),
    [MenuProps],
  );

  return (
    <Tooltip
      error={error}
      open={Boolean((error || tooltipContent) && tooltipOpen)}
      placement={tooltipPlacement}
      title={tooltipContent}
    >
      <sx.FormControl
        className={className}
        color={color}
        disabled={disabled}
        error={Boolean(error)}
        fullWidth={fullWidth}
        size={selectProps.size}
        onMouseDown={closeTooltip}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
      >
        {label ? (
          <sx.InputLabel color={color} htmlFor={id}>
            {label}
          </sx.InputLabel>
        ) : null}
        <MuiSelect
          {...selectProps}
          color={color}
          // We rely on the `renderValue` function to render adornments, so we need
          // displayEmpty to be true for when no value is selected
          displayEmpty
          inputProps={{ ...selectProps.inputProps, id }}
          MenuProps={menuProps}
          // Because we're using `displayEmpty`, we need to override MuiSelect setting `notched`
          notched={undefined}
          renderValue={renderValueCallback}
        />
      </sx.FormControl>
    </Tooltip>
  );
}
